// What the resend queue is holding (PRD 108 §4.4), on every field page: the
// presses kept on this phone and waiting for signal, with Send now; and the
// ones the server refused when they arrived — *Not recorded*, in its words
// and with the time they were pressed, so the technician can tell the
// office. Nothing here is ever resent as a new press.

import React, { useEffect, useState } from 'react';
import { XStack, YStack } from 'tamagui';
import { GBAlert, GBButton, GBText } from '@greenbackapp/ui';
import { dismissNotRecorded, drainQueue, onQueueChange, type QueueState } from './fieldWriteQueue';

/** The number of presses the server has accepted from the queue; a screen reloads when it moves. */
export function useQueueSent(): number {
  const [sent, setSent] = useState(0);
  useEffect(() => onQueueChange(state => setSent(state.sent)), []);
  return sent;
}

export const FieldQueueBanner: React.FC = () => {
  const [state, setState] = useState<QueueState | null>(null);
  useEffect(() => onQueueChange(setState), []);
  if (!state || (state.queue.length === 0 && state.notRecorded.length === 0)) return null;

  const waiting = state.queue.length;
  return (
    <YStack paddingHorizontal="$4" paddingTop="$3" gap="$2">
      {waiting > 0 && (
        <GBAlert
          variant="info"
          message={`${waiting} ${waiting === 1 ? 'change is' : 'changes are'} saved on this phone and will send when you're back online: ` +
            state.queue.map(entry => entry.label).join(', ') + '.'}
          action={state.sending ? undefined : { label: 'Send now', onPress: () => void drainQueue() }}
        />
      )}
      {state.notRecorded.length > 0 && (
        <YStack role="alert" gap="$1">
          <GBAlert
            variant="warning"
            title="Not recorded"
            message="These reached the office after something had changed, so they weren't saved. Tell the office what happened."
          />
          {state.notRecorded.map(entry => (
            <XStack key={entry.id} gap="$2" alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <GBText variant="bodySmall" flexShrink={1}>{`${entry.label}: ${entry.message}`}</GBText>
              <GBButton variant="ghost" size="sm" onPress={() => dismissNotRecorded(entry.id)}>Dismiss</GBButton>
            </XStack>
          ))}
        </YStack>
      )}
    </YStack>
  );
};

export default FieldQueueBanner;
